import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, UserPlus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import api from "@/lib/api";

/**
 * Searchable customer picker — fetches existing customers and lets you
 * search by name/mobile and select from a dropdown. onPick(customer|null).
 */
export default function CustomerSearchSelect({
  selectedId = null, onPick, placeholder = "Search existing customer…",
  testId, statusFilter,
}) {
  const [open, setOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const fetchCustomers = async () => {
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      const { data } = await api.get("/customers", { params });
      setCustomers(data || []);
    } catch (e) { console.error("Failed to fetch customers:", e); }
  };

  useEffect(() => { fetchCustomers(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (open) { fetchCustomers(); setTimeout(() => inputRef.current?.focus(), 0); }
    // eslint-disable-next-line
  }, [open]);
  useEffect(() => {
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setOpen(false); setSearch(""); }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const selected = customers.find((c) => c.id === selectedId);
  const s = search.toLowerCase();
  const filtered = customers.filter((c) =>
    c.name.toLowerCase().includes(s) || (c.mobile || "").toLowerCase().includes(s) || (c.email || "").toLowerCase().includes(s));

  const pick = (c) => { onPick(c); setOpen(false); setSearch(""); };

  return (
    <div ref={wrapperRef} className="relative">
      <Button
        type="button" variant="outline" onClick={() => setOpen((o) => !o)}
        className="w-full justify-between font-normal h-10 text-sm" data-testid={testId}
      >
        <span className={cn(!selected && "text-muted-foreground")}>
          {selected ? `${selected.name} · ${selected.mobile}` : placeholder}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-md shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={inputRef} type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name or mobile…"
              className="w-full h-8 px-2 text-sm border border-[#E2E8F0] rounded outline-none focus:border-[#0055FF]"
              data-testid={`${testId}-input`}
            />
          </div>
          <div className="max-h-56 overflow-auto py-1">
            <button
              type="button" onClick={() => pick(null)}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2 text-[#0055FF]"
              data-testid={`${testId}-manual`}
            >
              <UserPlus className="h-4 w-4" /> New customer (enter manually)
            </button>
            {filtered.map((c) => (
              <button
                type="button" key={c.id} onClick={() => pick(c)}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2"
                data-testid={`${testId}-opt-${c.id}`}
              >
                <Check className={cn("h-4 w-4", selectedId === c.id ? "opacity-100 text-[#0055FF]" : "opacity-0")} />
                <span className="flex-1 truncate">
                  <span className="font-medium">{c.name}</span>{" "}
                  <span className="text-[#64748B] font-mono text-xs">{c.mobile}</span>
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-muted-foreground">No customers found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
